<script>
  import Portal from 'svelte-portal';
  import {
    ChevronDown,
    ChevronUp,
    LoaderCircle,
    MapPin,
    MessageCircle,
    MoreHorizontal,
    Pin,
    PinOff
  } from 'lucide-svelte';

  export let rows = [];
  export let myId = null;
  export let loading = false;
  export let error = '';
  export let onJump = () => {};
  export let onUnpin = () => {};

  let expanded = false;
  let menu = null;

  $: if (!rows.length) {
    expanded = false;
    menu = null;
  }

  function getTypeLabel(row) {
    const labels = {
      audio: 'Tin nhắn thoại',
      album: 'Album ảnh',
      call: 'Cuộc gọi',
      location: 'Vị trí hiện tại',
      media: 'Tệp đính kèm',
      share: 'Tin nhắn được chia sẻ',
      sticker: 'Nhãn dán'
    };

    return labels[row?.type] || 'Tin nhắn';
  }

  function getPreview(row) {
    if (row?.status === 1) return 'Tin nhắn đã được thu hồi';
    if (row?.type !== 'text' && row?.type !== 'link') return getTypeLabel(row);
    return String(row?.message || '').trim() || getTypeLabel(row);
  }

  function getSender(row) {
    return String(row?.from) === String(myId) ? 'Bạn' : row?.fromName || `User ${row?.from || ''}`;
  }

  function openMenu(event, row) {
    event.stopPropagation();
    const rect = event.currentTarget.getBoundingClientRect();
    const width = 164;
    const height = 48;
    const padding = 10;
    const x = Math.max(padding, Math.min(rect.right - width, window.innerWidth - width - padding));
    const y = rect.bottom + height + padding > window.innerHeight ? rect.top - height - 6 : rect.bottom + 6;
    menu = { row, x, y };
  }

  function closeMenu() {
    menu = null;
  }

  function unpin(row) {
    closeMenu();
    onUnpin(row);
  }
</script>

<svelte:window on:click={closeMenu} on:keydown={(event) => event.key === 'Escape' && closeMenu()} />

{#if rows.length || loading || error}
  <section class:expanded class="pinned-panel" aria-label="Tin nhắn đã ghim">
    {#if rows.length && !expanded}
      <button class="pin-summary" type="button" on:click={() => (expanded = true)}>
        <span class="pin-icon"><Pin size={17} strokeWidth={2.1} /></span>
        <span class="summary-copy">
          <strong>{getTypeLabel(rows[0])}</strong>
          <span>{getSender(rows[0])}: {getPreview(rows[0])}</span>
        </span>
        <span class="pin-count">{rows.length > 1 ? `+${rows.length - 1} ghim` : '1 ghim'}</span>
        <ChevronDown size={16} />
      </button>
    {:else if rows.length}
      <div class="panel-header">
        <strong>Danh sách ghim ({rows.length})</strong>
        <button type="button" on:click={() => (expanded = false)}>
          Thu gọn <ChevronUp size={15} />
        </button>
      </div>

      <div class="pin-list">
        {#each rows as row (row.id)}
          <div class="pin-row">
            <button class="pin-content" type="button" on:click={() => onJump(row)}>
              <span class="row-icon">
                {#if row.type === 'location'}
                  <MapPin size={17} />
                {:else}
                  <MessageCircle size={17} />
                {/if}
              </span>
              <span class="row-copy">
                <strong>{getTypeLabel(row)}</strong>
                <span>{getSender(row)}: {getPreview(row)}</span>
              </span>
            </button>
            <button class="more-button" type="button" aria-label="Thao tác với tin nhắn ghim" on:click={(event) => openMenu(event, row)}>
              <MoreHorizontal size={18} />
            </button>
          </div>
        {/each}
      </div>
    {/if}

    {#if loading}
      <div class="panel-status"><LoaderCircle class="animate-spin" size={13} /> Đang đồng bộ</div>
    {:else if error}
      <div class="panel-error">{error}</div>
    {/if}
  </section>
{/if}

<Portal>
  {#if menu}
    <div
      class="pin-menu"
      style={`left:${menu.x}px; top:${menu.y}px`}
      on:click|stopPropagation>
      <button type="button" on:click={() => unpin(menu.row)}>
        <PinOff size={16} /> Bỏ ghim
      </button>
    </div>
  {/if}
</Portal>

<style>
  .pinned-panel {
    flex: 0 0 auto;
    position: relative;
    z-index: 8;
    margin: 0.45rem 0.55rem 0;
    overflow: hidden;
    border: 1px solid rgba(203, 213, 225, 0.92);
    border-radius: 0.65rem;
    background: rgba(255, 255, 255, 0.97);
    box-shadow: 0 4px 15px rgba(15, 23, 42, 0.07);
  }

  .pinned-panel.expanded {
    box-shadow: 0 10px 28px rgba(15, 23, 42, 0.12);
  }

  .pin-summary,
  .panel-header,
  .pin-row {
    display: flex;
    align-items: center;
  }

  .pin-summary {
    width: 100%;
    min-height: 3.05rem;
    gap: 0.65rem;
    padding: 0.45rem 0.7rem;
    text-align: left;
  }

  .pin-summary:hover,
  .pin-content:hover {
    background: #f8fafc;
  }

  .pin-icon,
  .row-icon {
    display: grid;
    flex: 0 0 auto;
    place-items: center;
    width: 1.85rem;
    height: 1.85rem;
    border-radius: 0.55rem;
    color: #ea580c;
    background: #fff7ed;
  }

  .summary-copy,
  .row-copy {
    min-width: 0;
    display: grid;
    flex: 1;
    gap: 0.08rem;
  }

  .summary-copy strong,
  .row-copy strong {
    font-size: 0.78rem;
    line-height: 1.15;
    color: #0f172a;
  }

  .summary-copy span,
  .row-copy span {
    overflow: hidden;
    color: #64748b;
    font-size: 0.7rem;
    line-height: 1.25;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .pin-count {
    flex: 0 0 auto;
    padding: 0.25rem 0.5rem;
    border: 1px solid #cbd5e1;
    border-radius: 0.4rem;
    color: #334155;
    font-size: 0.7rem;
    font-weight: 600;
  }

  .panel-header {
    justify-content: space-between;
    min-height: 2.1rem;
    padding: 0.35rem 0.7rem;
    background: #f1f5f9;
    color: #1e293b;
    font-size: 0.72rem;
  }

  .panel-header button {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    color: #475569;
  }

  .pin-list {
    max-height: min(14rem, 36vh);
    overflow-y: auto;
  }

  .pin-row + .pin-row {
    border-top: 1px solid #e2e8f0;
  }

  .pin-content {
    min-width: 0;
    display: flex;
    flex: 1;
    align-items: center;
    gap: 0.6rem;
    padding: 0.55rem 0.25rem 0.55rem 0.7rem;
    text-align: left;
  }

  .row-icon {
    color: #2563eb;
    background: #eff6ff;
  }

  .more-button {
    display: grid;
    flex: 0 0 auto;
    place-items: center;
    width: 2.35rem;
    height: 2.35rem;
    margin-right: 0.35rem;
    border-radius: 999px;
    color: #64748b;
  }

  .more-button:hover {
    color: #0f172a;
    background: #f1f5f9;
  }

  .panel-status,
  .panel-error {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.3rem;
    padding: 0.2rem 0.6rem;
    border-top: 1px solid #e2e8f0;
    color: #64748b;
    background: #f8fafc;
    font-size: 0.66rem;
  }

  .panel-error {
    color: #b91c1c;
    background: #fef2f2;
  }

  :global(.pin-menu) {
    position: fixed;
    z-index: 10000;
    width: 10.25rem;
    padding: 0.3rem;
    border: 1px solid #e2e8f0;
    border-radius: 0.65rem;
    background: #fff;
    box-shadow: 0 12px 30px rgba(15, 23, 42, 0.18);
  }

  :global(.pin-menu button) {
    width: 100%;
    display: flex;
    align-items: center;
    gap: 0.55rem;
    padding: 0.55rem 0.65rem;
    border-radius: 0.45rem;
    color: #dc2626;
    font-size: 0.78rem;
    text-align: left;
  }

  :global(.pin-menu button:hover) {
    background: #fef2f2;
  }

  @media (max-width: 640px) {
    .pinned-panel {
      margin: 0.35rem 0.35rem 0;
    }

    .pin-summary {
      gap: 0.5rem;
      padding-inline: 0.55rem;
    }

    .pin-count {
      padding-inline: 0.4rem;
    }
  }
</style>
